// SHADOW MARKET™ — Kids Academy main map page.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, Star, Trophy } from "lucide-react";
import { ASSETS } from "../assets";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLanguage } from "../i18n/LanguageContext";
import { Personaje, PERSONAJE_COLORS } from "../components/kids/Personaje";
import { LECCIONES_KIDS } from "../data/kidsLecciones";
import { getKidsUi } from "../i18n/kids-academy";
import { getKidsProgress, getKidsStars, isLeccionCompleted } from "../utils/kidsProgress";

const KidsAcademyPage = () => {
  const { lang } = useLanguage();
  const ui = getKidsUi(lang);
  const [progress, setProgress] = useState(() => getKidsProgress());

  useEffect(() => {
    // Re-read progress on focus (catch updates from lesson page)
    const onFocus = () => setProgress(getKidsProgress());
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-white">
      {/* Bright kid-friendly ambient gradients */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_0%,rgba(245,166,35,0.20),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_30%,rgba(0,212,255,0.18),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(0,200,100,0.18),transparent_50%)]" />
      </div>

      <header className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-10 md:py-4">
          <Link
            to="/academy"
            className="inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white"
            data-testid="kids-back-academy"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">{ui.back_to_academy}</span>
          </Link>
          <img src={ASSETS.logos.rebel} alt="REBEL" className="h-8 w-auto md:h-10" />
          <div className="flex items-center gap-3">
            <Link
              to="/kids/logros"
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300 hover:bg-amber-400/20"
              data-testid="kids-logros-link"
            >
              <Trophy className="h-3 w-3" />
              {ui.achievements}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-4 py-10 md:px-10">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10 text-center"
        >
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-orange-400/40 bg-orange-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-orange-300">
            🎮 {ui.age}
          </div>
          <h1
            className="font-display text-3xl font-black leading-tight tracking-tight md:text-6xl"
            style={{
              background: "linear-gradient(135deg, #f5a623 0%, #00c864 50%, #00d4ff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
            data-testid="kids-page-title"
          >
            {ui.page_title}
          </h1>
          <p className="mt-3 text-base text-white/70 md:text-lg">{ui.page_subtitle}</p>
          {/* Character lineup */}
          <div className="mt-6 grid grid-cols-5 items-end justify-items-center gap-1 md:hidden">
            {["satoshi", "hodl", "miner", "oracle", "bear"].map((id) => (
              <Personaje key={id} id={id} estado="idle" size={52} label />
            ))}
          </div>
          <div className="mt-6 hidden flex-wrap items-end justify-center gap-5 md:flex">
            {["satoshi", "hodl", "miner", "oracle", "bear"].map((id) => (
              <Personaje key={id} id={id} estado="idle" size={80} label />
            ))}
          </div>
        </motion.div>

        {/* Adventure Map */}
        <section className="mb-12">
          <h2 className="mb-4 text-[10px] font-bold uppercase tracking-[0.22em] text-orange-300">
            🗺 {ui.map_title}
          </h2>
          <div className="space-y-4">
            {LECCIONES_KIDS.map((l, i) => {
              const completed = isLeccionCompleted(l.id);
              const stars = getKidsStars(l.id);
              const locked = i > 0 && !isLeccionCompleted(LECCIONES_KIDS[i - 1].id);
              const c = PERSONAJE_COLORS[l.personaje] || PERSONAJE_COLORS.satoshi;
              const titulo = l.titulo[lang] || l.titulo.es;
              const Wrapper = locked ? "div" : Link;
              const wrapperProps = locked
                ? {}
                : { to: `/kids/leccion/${l.id}` };
              return (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  whileHover={!locked ? { x: 4 } : {}}
                  data-testid={`kids-leccion-card-${l.id}`}
                >
                  <Wrapper
                    {...wrapperProps}
                    className={`group flex items-center gap-3 rounded-2xl border p-3 backdrop-blur-xl transition-colors md:gap-4 md:p-5 ${
                      locked
                        ? "border-white/5 bg-white/[0.02] opacity-60"
                        : completed
                        ? "border-emerald-400/30 bg-emerald-400/[0.04] hover:border-emerald-400/50"
                        : "border-white/10 bg-white/[0.04] hover:border-orange-400/40"
                    }`}
                    style={{ display: "flex", textDecoration: "none" }}
                  >
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-black md:h-14 md:w-14"
                      style={{
                        background: locked ? "rgba(255,255,255,0.05)" : `${c.acento}15`,
                        border: `1px solid ${locked ? "rgba(255,255,255,0.1)" : c.acento + "55"}`,
                        color: locked ? "rgba(255,255,255,0.4)" : c.acento,
                      }}
                    >
                      {locked ? <Lock className="h-4 w-4 md:h-5 md:w-5" /> : <span className="md:text-lg">{l.nivel}</span>}
                    </div>
                    <div className="hidden shrink-0 sm:block">
                      <Personaje id={l.personaje} estado="idle" size={56} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/40 md:text-[10px] md:tracking-[0.2em]">
                        {ui.level} {l.nivel} · {ui.mission}
                      </div>
                      <div className="text-sm font-black leading-tight text-white md:text-lg">{titulo}</div>
                      {locked && (
                        <div className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-white/40">
                          🔒 {ui.locked}
                        </div>
                      )}
                      {completed && (
                        <div className="mt-1 flex items-center gap-1 text-amber-300">
                          {[1, 2, 3].map((s) => (
                            <Star
                              key={s}
                              className="h-3.5 w-3.5"
                              fill={s <= stars ? "currentColor" : "transparent"}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {!locked && (
                      <div
                        className="shrink-0 rounded-lg border px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest transition-transform group-hover:translate-x-1 md:px-3"
                        style={{ borderColor: c.acento + "55", color: c.acento, background: c.acento + "14" }}
                      >
                        {completed ? "↻" : "▶"}
                      </div>
                    )}
                  </Wrapper>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Parents footer */}
        <p className="text-center text-[11px] text-white/35">{ui.parents_note}</p>
      </main>
    </div>
  );
};

export default KidsAcademyPage;
